import * as React from 'react'

import { CopyIcon, RefreshCcwIcon, StarIcon, Trash2Icon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import LogisticsMapViewSetUpStore from '~/client/src/shared/components/LogisticsMapView/LogisticsMapViewSetUp.store'
import { WorkflowList } from '~/client/src/shared/components/WorkflowList'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import SitePermit from '~/client/src/shared/models/Permit'
import {
  toWorkflowGroupingKey,
  toWorkflowSortingKey,
} from '~/client/src/shared/utils/logisticsHelper'
import { NOOP } from '~/client/src/shared/utils/noop'
import { IStatusItem } from '~/client/src/shared/utils/permitStatusUtils'

import PopoverComponent from '../../../components/PopoverComponent/PopoverComponent'
import DesktopInitialState from '../../../stores/DesktopInitialState'
import DesktopEventStore from '../../../stores/EventStore/DesktopEvents.store'
import ConfirmBatchStatusUpdateDialog from './ConfirmBatchStatusUpdateDialog'
import LogisticsStore from './Logistics/Logistics.store'
import LogisticsListStore from './LogisticsList/LogisticsList.store'
import LogisticsMapStore from './LogisticsMap/LogisticsMap.store'
import StatusUpdateMenu from './StatusUpdateMenu'
import SubscribeUsersSelector from './SubscribeUsersSelector/SubscribeUsersSelector'

interface IProps {
  store: LogisticsStore
  mapStore: LogisticsMapStore
  logisticsMapViewSetUpStore: LogisticsMapViewSetUpStore

  eventsStore?: DesktopEventStore
  state?: DesktopInitialState

  logisticsListStore?: LogisticsListStore
}

// The panel covers the map's right edge, so the camera centres the pin in what stays visible.
// Kept in step with `.permit-side-view { width }` in Logistics/Materials/Attachments.scss.
const WORKFLOW_PANEL_WIDTH_PX = 400

@inject('eventsStore', 'state')
@observer
export default class LogisticsSideBar extends React.Component<IProps> {
  private readonly logisticsListStore: LogisticsListStore

  public constructor(props: IProps) {
    super(props)

    this.logisticsListStore = props.logisticsListStore
  }

  public render() {
    return (
      <div className="logistics-side-bar full-height overflow-auto no-grow">
        {this.renderWorkflowList()}
      </div>
    )
  }

  private renderWorkflowList() {
    const { logisticsListStore } = this.props

    return (
      <div className="col full-height">
        {this.deletionConfirmDialog}
        {this.renderConfirmSubscriptionDialog()}
        <ConfirmBatchStatusUpdateDialog
          logisticsListStore={logisticsListStore}
          eventsStore={this.props.eventsStore}
        />
        <WorkflowList
          workflows={logisticsListStore.workflowPermits}
          groupingKey={toWorkflowGroupingKey(logisticsListStore.groupingKey)}
          sortKey={toWorkflowSortingKey(logisticsListStore.sortState.columnKey)}
          sortOrder={logisticsListStore.sortState.order}
          selectedIds={logisticsListStore.selectedInstancesIds}
          onSelectionChange={logisticsListStore.setSelectedIds}
          activeWorkflowId={
            this.store.selectedWorkflow?.id ??
            this.store.selectedSharedEntity?.id
          }
          onWorkflowClick={this.onWorkflowRowClick}
          toolbarExtra={this.renderBulkActions()}
        />
      </div>
    )
  }

  private renderBulkActions(): React.ReactNode {
    const { logisticsListStore } = this.props
    const hasSelectedInstances =
      logisticsListStore.selectedInstancesIds.length > 0
    if (!hasSelectedInstances) return null

    const isAdmin = this.props.state.userActiveProjectSettings?.isSuperUser
    const canDuplicate = logisticsListStore.selectedInstancesIds.length === 1
    const duplicateTitle = canDuplicate
      ? Localization.translator.duplicate
      : Localization.translator.selectASingleWorkflowToDuplicate

    return (
      <div className="row x-end y-center">
        {isAdmin && (
          <>
            <PopoverComponent
              title={Localization.translator.subscribeSelected}
              content={close => (
                <SubscribeUsersSelector
                  selectedWorkflows={
                    logisticsListStore.getSelectedNonDraftPermits
                  }
                  onClose={close}
                />
              )}
            >
              <span
                className="tw:flex tw:items-center pointer text grey-40"
                title={Localization.translator.subscribeSelected}
              >
                <StarIcon className="tw:size-[18px]" />
              </span>
            </PopoverComponent>

            {logisticsListStore.getSelectedNonDraftPermits.length > 0 && (
              <PopoverComponent
                title={Localization.translator.updateSelectedWorkflowsStatusTo}
                content={close => (
                  <StatusUpdateMenu
                    logisticsListStore={logisticsListStore}
                    onStatusSelected={this.onStatusSelected}
                    onClose={close}
                  />
                )}
              >
                <span
                  className="tw:flex tw:items-center pointer text grey-40"
                  title={Localization.translator.updateStatus}
                >
                  <RefreshCcwIcon className="tw:size-[18px]" />
                </span>
              </PopoverComponent>
            )}
          </>
        )}
        <span className="tw:flex-none! tw:mr-2.5" title={duplicateTitle}>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={duplicateTitle}
            disabled={!canDuplicate}
            onClick={this.duplicateWorkflow}
          >
            <CopyIcon />
          </Button>
        </span>
        {isAdmin && (
          <Button
            variant="destructive-ghost"
            size="icon-sm"
            className="tw:flex-none! tw:mr-2.5"
            title={Localization.translator.deleteSelected}
            aria-label={Localization.translator.deleteSelected}
            onClick={logisticsListStore.openDeletionConfirmModal}
          >
            <Trash2Icon />
          </Button>
        )}
      </div>
    )
  }

  private onStatusSelected = (item: IStatusItem) => {
    this.props.logisticsListStore.openBatchStatusUpdateConfirmModal(item)
  }

  private duplicateWorkflow = () => {
    const { logisticsListStore } = this.props
    if (logisticsListStore.selectedInstancesIds.length !== 1) return
    logisticsListStore.duplicateWorkflow(
      logisticsListStore.selectedInstancesIds[0],
    )
  }

  private renderConfirmSubscriptionDialog() {
    const {
      shouldSubscriptionConfirmModalShow,
      closeSubscriptionConfirmModal,
      subscriptionConfirmMessage,
      handleChangeAssociations,
      subscriptionConfirmButtonText,
    } = this.props.logisticsListStore

    return (
      <ConfirmDialog
        isOpen={shouldSubscriptionConfirmModalShow}
        onCancelClicked={closeSubscriptionConfirmModal}
        onDoneClicked={handleChangeAssociations}
        doneTitle={subscriptionConfirmButtonText}
      >
        <div className="text large pre-line">{subscriptionConfirmMessage}</div>
      </ConfirmDialog>
    )
  }

  private get deletionConfirmDialog(): JSX.Element {
    const {
      isDeletionConfirmModalShown,
      closeDeletionConfirmModal,
      deleteWorkflows,
      isUpdating,
    } = this.props.logisticsListStore

    return (
      <ConfirmDialog
        isOpen={isDeletionConfirmModalShown}
        loading={isUpdating}
        onCancelClicked={isUpdating ? NOOP : closeDeletionConfirmModal}
        onDoneClicked={deleteWorkflows}
        title={Localization.translator.doYouWantToDeleteSelectedWorkflows}
        doneTitle={`${Localization.translator.yes}, ${Localization.translator.delete}`}
        useRedConfirmationButton
      >
        <div className="text large pre-line tw:pb-2.5">
          {
            Localization.translator
              .allDataRelatedToSelectedWorkflowsWillBeDeleted
          }
        </div>
      </ConfirmDialog>
    )
  }

  private get store(): LogisticsStore {
    return this.props.store
  }

  // Filters, which answer the same on every map view: the seam refuses here before it moves
  // anyone.
  private isWorkflowDrawnOnMap = (workflow: SitePermit): boolean =>
    this.props.mapStore.filteredPermits.some(w => w.id === workflow.id)

  // The map views the seam may switch to. Only what this page is configured to show, so a pin on
  // some unrelated globe leaves the user where they are.
  private get candidateGlobeIds(): string[] {
    return this.props.state.workflowsMapIdsList
      .map(c => c.globeViewId)
      .filter(Boolean)
  }

  private onWorkflowRowClick = (workflow: SitePermit): void => {
    this.store.showWorkflowViewDialog(workflow)
    // The shared path nulls selectedWorkflow, which LogisticsMap's reaction reads as "closed".
    if (this.store.selectedSharedEntity) {
      return
    }
    this.logisticsMapViewSetUpStore.focusWorkflowOnMap(workflow, {
      isDrawnOnMap: this.isWorkflowDrawnOnMap,
      candidateGlobeIds: this.candidateGlobeIds,
      chromeInsetRightPx: WORKFLOW_PANEL_WIDTH_PX,
      shouldOpenMapCard: false,
    })
  }

  private get logisticsMapViewSetUpStore(): LogisticsMapViewSetUpStore {
    return this.props.logisticsMapViewSetUpStore
  }
}
